import React, { useState } from 'react';
import type { AssessmentCategory } from '../content/assessments';

interface ProfileLibraryProps {
  categories: AssessmentCategory[];
}

/**
 * The profile library: categories first, profile names inside. One category
 * is open at a time. Each header is a real button with aria-expanded and the
 * panel is a labelled region, so on a phone it reads as an accordion (header,
 * panel, header, panel). From 900px the same DOM is laid out as a quiet
 * two-column index: the headers stack on the left and the open panel sits
 * beside them, placed by CSS grid rather than a second render.
 */
export default function ProfileLibrary({ categories }: ProfileLibraryProps) {
  const [open, setOpen] = useState<string>(categories[0]?.id ?? '');
  return (
    <div className="mk-lib" style={{ '--mk-lib-count': categories.length } as React.CSSProperties}>
      {categories.map((category) => {
        const expanded = open === category.id;
        const buttonId = `lib-${category.id}-button`;
        const panelId = `lib-${category.id}-panel`;
        return (
          <React.Fragment key={category.id}>
            <h3 className="mk-lib__head">
              <button
                type="button"
                id={buttonId}
                className="mk-lib__btn"
                aria-expanded={expanded}
                aria-controls={panelId}
                onClick={() => setOpen(expanded ? '' : category.id)}
              >
                <span>{category.title}</span>
                <span className="mk-lib__mark" aria-hidden="true">
                  {expanded ? '−' : '+'}
                </span>
              </button>
            </h3>
            <div id={panelId} role="region" aria-labelledby={buttonId} className="mk-lib__panel" hidden={!expanded}>
              <p className="mk-lib__desc t-body">{category.description}</p>
              <ul className="mk-lib__list">
                {category.profiles.map((profile) => (
                  <li key={profile.name}>
                    <span className="mk-lib__name">{profile.name}</span>
                    <p className="mk-lib__one t-body">{profile.summary}</p>
                  </li>
                ))}
              </ul>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
